<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { PageDefinition } from '@/types/page'
import { downloadBlob, parseCsv } from '@/utils/export'
import AppIcon from '@/components/AppIcon.vue'

const props = defineProps<{
  open: boolean
  definition: PageDefinition
}>()

const emit = defineEmits<{
  close: []
  import: [rows: Array<Record<string, unknown>>]
}>()

const input = ref<HTMLInputElement | null>(null)
const filename = ref('')
const headers = ref<string[]>([])
const parsedRows = ref<string[][]>([])
const error = ref('')
const busy = ref(false)

const fields = computed(() => (props.definition.formFields ?? []).filter(field => !['file', 'image', 'readonly'].includes(field.type) && !field.createOnly))
const previewRows = computed(() => parsedRows.value.slice(0, 5))
const validCount = computed(() => {
  if (!headers.value.length) return 0
  return parsedRows.value.filter(row => validateRow(row) === '').length
})
const invalidCount = computed(() => Math.max(0, parsedRows.value.length - validCount.value))

watch(() => props.open, open => {
  if (!open) reset()
})

function reset(): void {
  filename.value = ''
  headers.value = []
  parsedRows.value = []
  error.value = ''
  busy.value = false
  if (input.value) input.value.value = ''
}

function choose(): void {
  input.value?.click()
}

function template(): void {
  const labels = fields.value.map(field => field.label)
  const example = fields.value.map(field => {
    if (field.type === 'date') return '2026-10-18'
    if (field.type === 'number') return '1'
    if (field.type === 'money') return '29.00'
    if (field.type === 'select' || field.type === 'multiselect' || field.type === 'region' || field.type === 'user') return field.options?.[0]?.label ?? ''
    return ''
  })
  downloadBlob(`${props.definition.title}-导入模板.csv`, `\uFEFF${labels.join(',')}\r\n${example.join(',')}\r\n`)
}

function onFile(event: Event): void {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (!file) return
  error.value = ''
  if (!/\.csv$/i.test(file.name)) {
    error.value = '请选择 CSV 文件，模板可从本窗口下载'
    target.value = ''
    return
  }
  if (file.size > 2 * 1024 * 1024) {
    error.value = '文件不能超过 2MB，请拆分后重试'
    target.value = ''
    return
  }
  const reader = new FileReader()
  reader.onload = () => {
    const rows = parseCsv(String(reader.result ?? ''))
    const header = rows.shift() ?? []
    if (!header.length) {
      error.value = '文件没有表头，请使用模板填写'
      return
    }
    headers.value = header.map(item => item.trim())
    parsedRows.value = rows.filter(row => row.some(cell => String(cell).trim()))
    filename.value = file.name
    if (!parsedRows.value.length) error.value = '文件只有表头，没有可导入的数据'
  }
  reader.onerror = () => {
    error.value = '文件读取失败，请重新选择'
  }
  reader.readAsText(file, 'utf-8')
}

function keyForHeader(header: string): string {
  const field = fields.value.find(item => item.label === header || item.key === header)
  return field?.key ?? ''
}

function validateRow(row: string[]): string {
  for (const field of fields.value) {
    if (!field.required) continue
    const index = headers.value.findIndex(header => keyForHeader(header) === field.key)
    if (index < 0 || !String(row[index] ?? '').trim()) return `${field.label}缺失`
  }
  return ''
}

function importRows(): void {
  if (!parsedRows.value.length || error.value) return
  const rows = parsedRows.value.filter(row => validateRow(row) === '').map(row => {
    const payload: Record<string, unknown> = {}
    headers.value.forEach((header, index) => {
      const key = keyForHeader(header)
      if (!key) return
      const field = fields.value.find(item => item.key === key)
      const raw = String(row[index] ?? '').trim()
      if (!raw) return
      if (field?.type === 'number') payload[key] = Number(raw)
      else if (field?.type === 'money') payload[key] = Math.round(Number(raw) * 100)
      else payload[key] = raw
    })
    return payload
  })
  if (!rows.length) {
    error.value = '没有通过校验的数据，请检查必填字段后重试'
    return
  }
  busy.value = true
  window.setTimeout(() => {
    emit('import', rows)
    busy.value = false
    emit('close')
  }, 420)
}
</script>

<template>
  <el-dialog :model-value="open" width="620px" align-center :show-close="false" class="import-dialog" @close="emit('close')">
    <template #header>
      <div class="import-dialog__head">
        <div>
          <h2>批量导入{{ definition.title }}</h2>
          <p>先在浏览器内解析并逐行校验，确认后才写入原型内存；刷新页面即重置。</p>
        </div>
        <button type="button" class="icon-button" aria-label="关闭" @click="emit('close')"><AppIcon name="close" :size="17" /></button>
      </div>
    </template>
    <div class="import-dialog__body">
      <div class="import-drop" :class="{ 'is-filled': filename }" @click="choose">
        <span class="import-drop__icon"><AppIcon :name="filename ? 'check-circle' : 'upload'" :size="22" /></span>
        <b>{{ filename || '选择 CSV 文件' }}</b>
        <small>{{ filename ? `已解析 ${parsedRows.length} 行，${validCount} 行可导入` : '支持 UTF-8 CSV，最大 2MB；也可先下载模板' }}</small>
        <input ref="input" type="file" accept=".csv,text/csv" hidden @change="onFile" />
      </div>
      <div class="import-dialog__tools">
        <button type="button" class="btn btn--sm" @click="template"><AppIcon name="download" :size="13" />下载模板</button>
        <span v-if="filename" class="import-summary">
          可导入 <b>{{ validCount }}</b> 行
          <em v-if="invalidCount">· {{ invalidCount }} 行待修正</em>
        </span>
      </div>
      <div v-if="error" class="info-banner info-banner--danger">{{ error }}</div>
      <div v-if="previewRows.length" class="import-preview">
        <div class="import-preview__title">数据预览 <small>前 5 行</small></div>
        <div class="table-scroll">
          <table class="pro-table import-table">
            <thead><tr><th v-for="header in headers" :key="header">{{ header }}</th></tr></thead>
            <tbody>
              <tr v-for="(row, index) in previewRows" :key="index">
                <td v-for="(cell, cellIndex) in row" :key="cellIndex">{{ cell || '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <template #footer>
      <div class="import-dialog__foot">
        <span>导入后会进入任务中心，失败行可单独重试。</span>
        <button type="button" class="btn" @click="emit('close')">取消</button>
        <button type="button" class="btn btn--primary" :disabled="!validCount || busy" @click="importRows">
          {{ busy ? '导入中…' : `确认导入 ${validCount} 行` }}
        </button>
      </div>
    </template>
  </el-dialog>
</template>

<style scoped>
.import-dialog__head { display: flex; align-items: flex-start; gap: 10px; }
.import-dialog__head h2 { margin: 0; font-size: 16px; color: var(--c-ink); }
.import-dialog__head p { margin: 4px 0 0; font-size: 10.5px; color: var(--c-muted); line-height: 1.55; }
.import-dialog__head .icon-button { margin-left: auto; }
.import-drop { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px; min-height: 132px; border: 1px dashed var(--c-border-strong); border-radius: 12px; background: var(--c-surface-muted); cursor: pointer; text-align: center; padding: 16px; }
.import-drop:hover { border-color: var(--c-brand); background: var(--c-brand-mist); }
.import-drop.is-filled { border-color: var(--c-success-border); background: var(--c-brand-mist); }
.import-drop__icon { width: 42px; height: 42px; display: grid; place-items: center; border-radius: 12px; background: #fff; border: 1px solid var(--c-border); color: var(--c-brand-strong); margin-bottom: 4px; }
.import-drop b { font-size: 12px; color: var(--c-text); }
.import-drop small { font-size: 10px; color: var(--c-muted); }
.import-dialog__tools { display: flex; align-items: center; gap: 8px; margin: 12px 0 8px; }
.import-summary { margin-left: auto; font-size: 10.5px; color: var(--c-muted); }
.import-summary b { color: var(--c-brand-strong); }
.import-summary em { font-style: normal; color: var(--c-danger); }
.import-preview { border: 1px solid var(--c-border); border-radius: 9px; overflow: hidden; margin-top: 10px; }
.import-preview__title { padding: 8px 11px; font-size: 11px; font-weight: 600; background: var(--c-surface-muted); border-bottom: 1px solid var(--c-divider); }
.import-preview__title small { color: var(--c-muted); font-weight: 400; margin-left: 4px; }
.import-table { min-width: 620px; }
.import-table td { height: 36px; font-size: 10.5px; white-space: nowrap; }
.import-dialog__foot { display: flex; align-items: center; gap: 8px; }
.import-dialog__foot > span { margin-right: auto; font-size: 9.5px; color: var(--c-faint); }
</style>
